import dayjs from "dayjs";
import { useNavigate } from "react-router";
import {
  IChallenge,
  IChallengeCompletion,
  usePartnerChallenges,
} from "../../../store/usePartnerChallenges";

/** Condition types whose target and threshold are amounts in cents. */
const VALUE_CHALLENGE_TYPES: IChallenge["conditions"]["type"][] = [
  "DROP_VALUE",
  "ITEM_TYPE_VALUE",
  "TOMRA_ITEM_TYPE_VALUE",
];

const formatEuro = (cents: number) => `€${(cents / 100).toFixed(2)}`;

interface Props {
  /** Unknown until the challenge has loaded; amounts then show raw. */
  challengeType?: IChallenge["conditions"]["type"];
}

/**
 * The earned cycles of a recurring challenge, one row each.
 *
 * The legacy Completed Challenges table reads `progress.completed` on the
 * progress document — a single flag that a recurring challenge never sets,
 * because it can be finished again tomorrow. Everything a support agent needs
 * to answer "did this customer get their reward" lives in the completion
 * ledger instead: which cycle, when it was earned, and what it paid out or
 * that it is still waiting on voucher stock.
 */
export const ChallengeCompletionsTable = ({ challengeType }: Props) => {
  /** Hooks */
  const navigate = useNavigate();
  const { challengeCompletions } = usePartnerChallenges();

  const isValueType = Boolean(
    challengeType && VALUE_CHALLENGE_TYPES.includes(challengeType)
  );

  const customerName = (completion: IChallengeCompletion) => {
    const name = [completion?.user?.first_name, completion?.user?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || completion?.user?.email || "Deleted user";
  };

  // A value challenge counts cents, which read as nonsense unformatted.
  const formatTarget = (amount?: number) => {
    if (amount == null) return "-";
    return isValueType ? formatEuro(amount) : amount;
  };

  // A row without a rewardType predates the other reward types: a voucher.
  const rewardLabel = (completion: IChallengeCompletion) => {
    const value = completion?.rewardValueSnapshot;
    if (completion?.rewardType === "CASH") {
      return value == null ? "-" : formatEuro(value);
    }
    if (completion?.rewardType === "DROPPIES") {
      return value == null ? "-" : `${value} Droppies`;
    }
    return completion?.voucherIssued?.code ?? "-";
  };

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Customer
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Cycle
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Target
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Threshold
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Earned
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Status
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Reward
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Issued
            </th>
          </tr>
        </thead>
        <tbody>
          {challengeCompletions?.map((completion) => {
            const isIssued = completion?.rewardStatus === "ISSUED";
            return (
              <tr
                onClick={() => {
                  if (completion?.user?._id) {
                    navigate(`/edit-user/${completion.user._id}`);
                  }
                }}
                key={completion._id}
                className="bg-white hover:bg-gray-100 cursor-pointer transition ease-linear border-b"
              >
                <td className="px-2 sm:px-6 py-1 sm:py-4 break-all sm:whitespace-nowrap">
                  {customerName(completion)}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4">
                  #{completion?.cycleNumber}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {/* The target as it stood when the cycle was earned, not as
                      it stands now — an admin may have changed it since. */}
                  {formatTarget(completion?.targetSnapshot)}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {/* The cumulative progress that earned this cycle. */}
                  {formatTarget(
                    completion?.thresholdParcelCount ??
                      completion?.thresholdValue
                  )}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap">
                  {completion?.earnedAt
                    ? dayjs(completion.earnedAt).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </td>
                <td
                  className={`px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap ${
                    isIssued ? "text-green-500" : "text-orange-500"
                  }`}
                >
                  {isIssued ? "Issued" : "Pending stock"}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {rewardLabel(completion)}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap">
                  {completion?.issuedAt
                    ? dayjs(completion.issuedAt).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
